import { addMinutes, isBefore } from 'date-fns';
import type { Box, Shipment, TemperatureMeasurement } from 'graphql-models';
import { randomId, randomUuid, type Factory } from 'walden-fast/types/mocks';
import { boxFactory } from './box';
import { consigneeFactory } from './consignee';
import {
  batteryMeasurementFactory,
  locationMeasurementFactory,
  temperatureMeasurementFactory,
} from './measurements';
import { pictureFactory } from './picture';
import { clientFactory } from './client';
import { sourceSystemFactory } from './source-system';
import { shipmentStateFactory } from './shipment-state';

const MOCK_SHIPMENT: Shipment = {
  id: '1',
  barcodeExternal: '123456789',
  // supplierCode: '0032442',
  updatedAt: new Date('Mon Mar 21 2025 15:15:35 GMT+0100').toISOString(),
  deliveredAt: new Date('Mon Mar 24 2025 15:11:35 GMT+0100').toISOString(),
  minTemperature: 0,
  maxTemperature: 15,
  alerts: [],
  weightKg: 122.12,
  pictures: [pictureFactory({ id: '1' }), pictureFactory({ id: '2' })],
  temperatureMeasurements: [
    temperatureMeasurementFactory({
      valueCelsius: 3.6,
      measuredAt: new Date('Mon Mar 21 2025 15:11:35 GMT+0100').toISOString(),
    }),
    temperatureMeasurementFactory({
      valueCelsius: 5.1,
      measuredAt: new Date('Mon Mar 21 2025 15:12:35 GMT+0100').toISOString(),
    }),
    temperatureMeasurementFactory({
      valueCelsius: 6.7,
      measuredAt: new Date('Mon Mar 21 2025 15:13:35 GMT+0100').toISOString(),
    }),
    temperatureMeasurementFactory({
      valueCelsius: 7.2,
      measuredAt: new Date('Mon Mar 21 2025 15:14:35 GMT+0100').toISOString(),
    }),
    temperatureMeasurementFactory({
      valueCelsius: 6.1,
      measuredAt: new Date('Mon Mar 21 2025 15:15:35 GMT+0100').toISOString(),
    }),
    temperatureMeasurementFactory({
      valueCelsius: 5.1,
      measuredAt: new Date('Mon Mar 21 2025 15:16:35 GMT+0100').toISOString(),
    }),
  ],
  currentBattery: batteryMeasurementFactory({
    percent: 78.2,
  }),
  currentTemperature: temperatureMeasurementFactory({
    valueCelsius: 5.1,
    measuredAt: new Date('Mon Mar 21 2025 15:16:35 GMT+0100').toISOString(),
  }),
  consignee: consigneeFactory({
    id: '1',
    name: 'Pharmacie de la Mairie',
  }),
  currentLocation: locationMeasurementFactory({
    longitude: 5.04148,
    latitude: 47.322048,
  }),
  locationMeasurements: [
    locationMeasurementFactory({ longitude: -1.057169, latitude: 45.916157 }),
    locationMeasurementFactory({ longitude: 1.86856, latitude: 46.16914 }),
    locationMeasurementFactory({ longitude: 4.835659, latitude: 45.764042 }),
    locationMeasurementFactory({ longitude: 5.04148, latitude: 47.322048 }),
    locationMeasurementFactory({ longitude: 2.354314, latitude: 48.857705 }),
  ],
  state: 'DELIVERED',
  createdAt: new Date('Mon Mar 21 2025 15:16:35 GMT+0100').toISOString(),
  client: clientFactory(),
  orderReference: '123456',
  parcelId: 'SHIP-AABBAABB-123456',
  sourceSystem: sourceSystemFactory(),
  orderId: 'cjsncjnljcnsfljvnfsv',
  parcelSequenceNumber: 3,
  parcelTotal: 5,
  shipmentStates: [
    shipmentStateFactory({ state: 'order_received' }),
    shipmentStateFactory({ state: 'order_packed' }),
    shipmentStateFactory({ state: 'shipping_in_progress' }),
  ],
  externalUuid: '01992ded-29b0-75b1-8d9e-947644374db9',
};

export const shipmentFactory: Factory<
  Shipment & { forBox: (box?: Partial<Box>) => Shipment }
> = (overrides) => ({
  ...MOCK_SHIPMENT,
  id: randomId(),
  externalUuid: randomUuid(),
  ...overrides,
  forBox: (box) => ({
    ...MOCK_SHIPMENT,
    id: randomId(),
    ...overrides,
    box: boxFactory(box),
  }),
});

export const temperatureSeries = (
  startDate: Date,
  start: number = 5,
  min: number = 2,
  max: number = 8,
  breach?: number,
) => {
  const result: TemperatureMeasurement[] = [];
  let currentDate = new Date(startDate);
  const today = new Date();

  while (isBefore(currentDate, today)) {
    let randomValue = start + Math.random() - 0.5;
    randomValue = Math.max(min, Math.min(max, randomValue)); // Clamp value within min and max

    result.push(
      temperatureMeasurementFactory({
        valueCelsius: randomValue,
        measuredAt: new Date(currentDate).toISOString(),
      }),
    );

    currentDate = addMinutes(currentDate, 1);
  }

  // If a breach value is provided and the array has values, set it as last value
  if (breach !== undefined && result.length > 0) {
    const lastIndex = result.length - 1;
    if (result[lastIndex]) {
      result[lastIndex].valueCelsius = breach;
    }
  }

  return result;
};
